import { SOCKET_EVENTS } from '@utils/constants';
import { ENABLE_RECEPTION_GATE } from '@utils/featureFlags';
import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';

type WebSocketEvent = (typeof SOCKET_EVENTS)[keyof typeof SOCKET_EVENTS];

export interface WebSocketContextType {
  emit: (message: string) => void;
  subscribeEvent: <K>(eventId: WebSocketEvent, callback: (data: K) => void) => void;
  unsubscribeEvent: <K>(eventId: WebSocketEvent, callback: (data: K) => void) => void;
}

export const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

interface WebSocketProviderProps {
  allowConnection?: boolean;
  url: string;
  commerceId: string;
  accountId?: string;
  // Reception gate (multisucursal): true sólo si el usuario es root Y el comercio tiene el
  // módulo PEDIDOS_MULTISUCURSAL (mismo predicado que el silenciado en el FE). Opcional para
  // mantener backward-compat; sólo se envía al backend cuando ENABLE_RECEPTION_GATE está ON.
  canSeeOtherBranches?: boolean;
  children: React.ReactNode;
}

interface SubscribePayload {
  channelIds: string[];
  id_comercio?: string;
  can_see_other_branches?: boolean;
}

const subscribers = new Map<WebSocketEvent, ((data: Record<string, unknown>) => void)[]>();

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  url,
  children,
  commerceId,
  accountId,
  allowConnection,
  canSeeOtherBranches = false,
}) => {
  const [webSocket, setWebSocket] = useState<WebSocket | null>(null);
  // Vía ref para leer el valor más reciente dentro de onopen SIN agregar canSeeOtherBranches al
  // dep array del efecto de conexión. Si estuviera en las deps, un cambio false→true (al cargar el
  // permiso multisucursal) re-ejecutaría el efecto: el cleanup limpia el keepAliveInterval pero el
  // re-run NO lo re-arma (onopen no dispara en un socket ya abierto) → el socket se caería por
  // timeout (~9.5min). Mismo patrón de ref que useIncomingOrder.
  const canSeeOtherBranchesRef = useRef(canSeeOtherBranches);
  useEffect(() => {
    canSeeOtherBranchesRef.current = canSeeOtherBranches;
  }, [canSeeOtherBranches]);

  const createWebSocket = () => {
    const socket = new WebSocket(url);

    setWebSocket(socket);
  };

  const emit = useCallback(
    (message: string) => {
      if (webSocket && webSocket.readyState === WebSocket.OPEN) {
        webSocket.send(JSON.stringify(message));
      }
    },
    [webSocket],
  );

  const subscribeEvent: WebSocketContextType['subscribeEvent'] = (eventId, callback) => {
    if (subscribers.has(eventId)) {
      const subs = subscribers.get(eventId) || [];
      subs.push(callback as (data: Record<string, unknown>) => void);
    } else {
      subscribers.set(eventId, [callback as (data: Record<string, unknown>) => void]);
    }
  };

  const unsubscribeEvent: WebSocketContextType['unsubscribeEvent'] = (eventId, callback) => {
    if (subscribers.has(eventId)) {
      const subs = subscribers.get(eventId) || [];
      const index = subs.indexOf(callback as (data: Record<string, unknown>) => void);
      if (index !== -1) {
        subs.splice(index, 1);
      }
    }
  };

  useEffect(() => {
    if (!webSocket) return;
    return () => {
      if (webSocket.readyState === WebSocket.OPEN) {
        webSocket.close(1000);
        subscribers.clear();
        setWebSocket(null);
      }
    };
  }, [webSocket]);

  useEffect(() => {
    if (!allowConnection) return;
    if (!commerceId || !url) return;
    let keepAliveInterval: ReturnType<typeof setInterval> | null = null;

    if (!webSocket) {
      createWebSocket();
      return;
    }

    webSocket.onopen = () => {
      const channelIds: string[] = [`panel_${commerceId}`, accountId && `panel_${accountId}`].filter(
        Boolean,
      ) as string[];

      // Default OFF (ENABLE_RECEPTION_GATE === false): el mensaje queda idéntico al actual
      // (un array stringificado), por lo que no cambia la recepción de eventos de ningún perfil.
      // ON: enviamos un objeto con channelIds + identidad de la conexión. El backend
      // (subscribe.js) ya soporta message_obj.channelIds, así que es backward-compatible.
      const message = ENABLE_RECEPTION_GATE
        ? JSON.stringify({
            channelIds,
            id_comercio: commerceId,
            can_see_other_branches: canSeeOtherBranchesRef.current,
          } satisfies SubscribePayload)
        : JSON.stringify(channelIds);

      webSocket?.send(
        JSON.stringify({
          action: 'subscribe',
          message,
        }),
      );

      keepAliveInterval = setInterval(
        () => {
          webSocket?.send(JSON.stringify({ action: 'checkConnection', message: 'testConnection' }));
        },
        9.5 * 60 * 1000,
      );
    };

    webSocket.onclose = (ev) => {
      if (ev.code !== 1000) {
        createWebSocket();
      }
    };

    webSocket.onerror = () => {
      toast.error('Error en el servidor de eventos');
    };

    webSocket.onmessage = (event) => {
      if (!event.data) return;

      const data = JSON.parse(event.data);
      const eventId = data?.eventId || '';
      if (subscribers.has(eventId)) {
        const subs = subscribers.get(eventId) || [];
        subs.forEach((callback) => callback(data));
      }
    };

    return () => {
      if (keepAliveInterval) {
        clearInterval(keepAliveInterval);
      }
    };
  }, [url, commerceId, accountId, webSocket, allowConnection]);

  const contextValue: WebSocketContextType = { subscribeEvent, unsubscribeEvent, emit };

  return <WebSocketContext.Provider value={contextValue}>{children}</WebSocketContext.Provider>;
};
